import { invoke } from '@tauri-apps/api/core';
import { DB_MANAGER_EXTENSION_ID, DEFAULT_DB_PORTS } from '../dbEngines';
import { requireLocalCapacity } from '../../plans/localGate';
import { capRefusal, isCapped } from '../../plans/capRefusal';
import i18n from '../../i18n';
import { useNotificationStore } from '../../store/notificationStore';

/**
 * DB Manager connections, read from the vault.
 *
 * Connections used to live in the extension's own storage.json with passwords
 * in the OS keychain. The vault already has a `dbConnection` type — encrypted,
 * team-shareable, and the same place SFTP connections live — so this is where
 * they belong.
 *
 * Scoped to the DB Manager alone. A third-party extension holding a db:*
 * capability keeps its own storage: the vault is the user's, not something an
 * arbitrary extension gets to enumerate.
 *
 * Nothing here decrypts until it has to. vault_list returns envelopes with an
 * index (engine, host, port, username, database) — enough to draw a list.
 * The PASSWORD never comes here at all (card 6.4): `vault_db_connection_info`
 * answers every other field plus `hasPassword`, saving and "remember this
 * password" decide it in Rust, and `db_connect_vault` fills it in Rust.
 */

export const VAULT_LOCKED = 'vault_locked';

/**
 * The vault form's "Require SSL" checkbox (`data.ssl`), as a TLS floor.
 *
 * Until 24.09.2026 the checkbox was saved and never read: a connection marked
 * "Require SSL" dialled with sqlx's default, `prefer`, which silently falls
 * back to plaintext when the server offers no TLS. Now:
 *
 *   * unchecked  -> nothing changes. No `sslMode` is invented, so Rust keeps
 *                   its default, `prefer` — the deliberate DB-TLS default
 *                   (vault hardening PR #20) stays exactly as it was;
 *   * checked    -> at least `require` (postgres sslmode=require, MySQL
 *                   REQUIRED — both engines take the one name, see
 *                   parse_ssl_mode in db_postgres.rs / db_mysql.rs).
 *
 * A floor, not an override: a mode the DB Manager stored that is already
 * stronger (verify_ca / verify_full) is kept, and a weaker one (disable /
 * allow / prefer) is raised, so a ticked "Require SSL" can never connect
 * without TLS. An unrecognised mode is left alone — Rust refuses it loudly,
 * which is better than guessing. SQLite has no TLS and never gets here.
 */
const BELOW_REQUIRE = new Set(['', 'disable', 'allow', 'prefer']);
export const REQUIRE_SSL_MODE = 'require';

export function sslModeWithVaultFloor(sslMode, requireSsl) {
  const mode = typeof sslMode === 'string' ? sslMode.trim() : '';
  if (requireSsl !== true) return mode;
  return BELOW_REQUIRE.has(mode.toLowerCase()) ? REQUIRE_SSL_MODE : mode;
}

/** Vault items are named by the user; these are internal and hidden, per FTP. */
const isHidden = (item) => String(item?.name || '').startsWith('__');

export function isDbManager(extensionId) {
  return extensionId === DB_MANAGER_EXTENSION_ID;
}

/**
 * dbConnection envelopes, or null when the vault cannot answer.
 *
 * null means "no answer" — locked, or not running under Tauri — and is
 * deliberately distinct from [] ("unlocked, and you have none"). Callers that
 * conflate them would show an empty-state to someone whose connections are
 * merely behind a lock.
 */
export async function listDbConnectionEnvelopes() {
  try {
    const status = await invoke('vault_status');
    if (!status?.unlocked) return null;
    const list = await invoke('vault_list');
    return (Array.isArray(list) ? list : []).filter((it) => it?.type === 'dbConnection' && !isHidden(it));
  } catch {
    // An unreachable vault is the same as a locked one as far as the caller is
    // concerned: it has nothing to show and should say so rather than throw.
    return null;
  }
}

export async function isDbVaultLocked() {
  try {
    const status = await invoke('vault_status');
    return !status?.unlocked;
  } catch {
    return true;
  }
}

/**
 * A vault item as the descriptor shape the rest of the DB Manager speaks.
 *
 * The one rename to watch: the vault calls it `username`, every DB Manager
 * path calls it `user`. Getting this wrong produces a connection that dials
 * with an empty user and fails authentication — which looks like a wrong
 * password, not a mapping bug.
 *
 * `remember` reports whether a password is actually stored. A `dbConnection`
 * item may legitimately omit it, which means "ask when connecting" — so this
 * checks for the KEY, not for a truthy value: a stored empty password is a
 * real answer and must not be reported as absent.
 */
export function descriptorFromVaultItem(item, { data } = {}) {
  if (!item) return null;
  const source = data || item.data || item.index || {};
  const engine = source.engine || 'mysql';
  // `hasPassword` from Rust (card 6.4: the password itself never comes here).
  const hasPassword = !!data && (item.hasPassword ?? Object.prototype.hasOwnProperty.call(data, 'password'));

  const descriptor = {
    id: item.id,
    name: item.name || '',
    engine,
    origin: 'vault',
    // The list-index descriptor has no `ssl` (nor any TLS field): it must never
    // be used to connect — the connect path re-reads the full item.
    // Built from the LIST index, which carries no password, no TLS settings
    // and — for sqlite — not even the path (see derive_index in vault.rs).
    // Anything reading this must not treat an absent field as an answer, and
    // anything WRITING it must merge rather than replace. Without the flag the
    // first save of a session rebuilt each item from an index and silently
    // dropped every stored password.
    partial: !data,
  };

  if (engine === 'sqlite') {
    descriptor.path = source.path || '';
    descriptor.remember = false;
    return descriptor;
  }

  descriptor.host = source.host || 'localhost';
  descriptor.port = String(source.port || DEFAULT_DB_PORTS[engine] || '');
  descriptor.user = source.username || '';
  descriptor.database = source.database || '';
  // Only meaningful once the item has been decrypted; a list-only descriptor
  // reports false and the connect path re-reads the item anyway.
  descriptor.remember = hasPassword;
  // Reserved by the plan for when the TLS fields move into the vault. They
  // round-trip through __rest today, so copying them costs nothing and means
  // a vault-stored connection is not silently less secure than a legacy one.
  for (const key of ['sslMode', 'sslRootCert', 'sslClientCert', 'sslClientKey']) {
    if (source[key]) descriptor[key] = source[key];
  }
  // "Require SSL" from the vault form. Every connect path (auto-connect, the
  // Reconnect button, the guest's form) starts from this descriptor, so the
  // floor applied here reaches all of them through applyTlsFields.
  const floored = sslModeWithVaultFloor(descriptor.sslMode, source.ssl);
  if (floored) descriptor.sslMode = floored;
  return descriptor;
}

/** The decrypted item WITHOUT its password (+ `hasPassword`), or a VAULT_LOCKED error. */
export async function readDbConnectionItem(itemId) {
  const full = await invoke('vault_db_connection_info', { id: itemId }).catch((e) => {
    throw new Error(String(e?.message || e) || VAULT_LOCKED);
  });
  if (!full) return null;
  return full;
}

/**
 * Store (or clear) the password on an existing vault connection.
 *
 * This is what "remember this password" now means: it goes into the encrypted
 * item alongside the rest of the connection, not into the OS credential store.
 * One place holds the connection, one thing to lock, one thing to share with a
 * team.
 *
 * Passing null REMOVES the key, which is how a connection is switched back to
 * asking every time — an item with no password key is the signal the connect
 * path reads. Writing '' instead would silently mean "the password is empty".
 */
export async function setDbConnectionPassword(itemId, password) {
  // In Rust (card 6.4): the stored item, password included, never comes here.
  await invoke('vault_db_set_password', { id: itemId, password: password == null ? null : password });
}

/**
 * DB Manager connection ids (the guest's own, before the vault mints one)
 * whose save the Free cap refused this session. See saveDbConnectionToVault.
 */
const capRefusedThisSession = new Set();

/**
 * Saved database connections, counted for the plan's cap. A LOCKED vault
 * rejects with `vault_locked`, which the host bridge answers with the unlock
 * gate and then retries the save (counting again). A vault that is unlocked
 * but cannot be listed is NOT room: the save is refused with the sentence
 * guardNewVaultItems (store/vaultStore.js) uses.
 */
async function countDbConnectionsForCap() {
  let status = null;
  try {
    status = await invoke('vault_status');
  } catch {
    status = null;
  }
  if (status && !status.unlocked) throw new Error(VAULT_LOCKED);
  let list = null;
  try {
    list = status ? await invoke('vault_list') : null;
  } catch {
    list = null;
  }
  if (!Array.isArray(list)) {
    const message = i18n.t('plans.localLimit.countFailed');
    // The DB Manager keeps a connection it could not save for the session and
    // says nothing itself, so the sentence is shown here.
    useNotificationStore.getState().addNotification({ type: 'warning', message, duration: 0 });
    throw Object.assign(new Error(message), { planLimit: true });
  }
  return list.filter((it) => it?.type === 'dbConnection' && !isHidden(it)).length;
}

/**
 * Create or update a connection in the vault.
 *
 * Takes a DB Manager descriptor and writes the vault's shape — the `user` →
 * `username` rename lives here, in one direction, next to the one that reads
 * it back.
 *
 * A name is required by the vault and optional in the DB Manager, so one is
 * synthesised from the connection itself rather than rejecting the save. An
 * unnamed row in a credential list is worse than a generated label.
 */
export async function saveDbConnectionToVault(descriptor) {
  if (!descriptor) throw new Error('no_connection');
  // Plans v2: Free keeps 1 saved database connection. A new one past the cap
  // opens the upgrade; the connection still works for this session (the
  // guest keeps it in memory), it is just not saved.
  const isNew = !(descriptor.origin === 'vault' && descriptor.id);
  if (isNew && isCapped('dbConnections')) {
    const used = await countDbConnectionsForCap();
    // A connection this session already refused stays session-only without
    // the upgrade opening again on every later save (edit, reconnect); it is
    // saved as soon as there is room.
    const silent = descriptor.id != null && capRefusedThisSession.has(String(descriptor.id));
    if (!requireLocalCapacity('dbConnections', used, { silent })) {
      if (descriptor.id != null) capRefusedThisSession.add(String(descriptor.id));
      throw Object.assign(new Error(capRefusal('dbConnections', used) || 'plan_limit'), { planLimit: true });
    }
  }
  const engine = descriptor.engine || 'mysql';
  const isFile = engine === 'sqlite';

  // ALWAYS start from what is already stored, whether the descriptor is
  // partial or not.
  //
  // A dbConnection item carries keys the DB Manager does not model at all: the
  // Vault panel gives it a `folder` and `notes`, and `folder` is owned by every
  // type (VaultItemModal's OWNED_DATA_KEYS). `vault_put_item` replaces the
  // encrypted body wholesale — there is no server-side merge — so rebuilding
  // `data` from the descriptor alone un-files the item and destroys its notes,
  // silently and with no undo, on the commonest operation there is. That is the
  // same invariant VaultItemModal states as "keeps every data key the form does
  // not own".
  //
  // Merging unconditionally is safe because everything the DB Manager DOES own
  // is written below without a condition, and the two things that can
  // legitimately be REMOVED — the password and the TLS settings — each say so
  // explicitly rather than by staying silent.
  let stored = null;
  if (descriptor.origin === 'vault' && descriptor.id) {
    const existing = await invoke('vault_db_connection_info', { id: descriptor.id }).catch(() => null);
    if (existing?.type === 'dbConnection') stored = existing.data || {};
  }
  const base = stored ? { ...stored } : {};

  const data = isFile
    ? { ...base, engine, ...(descriptor.path ? { path: descriptor.path } : {}) }
    : {
        ...base,
        engine,
        host: descriptor.host || 'localhost',
        port: parseInt(descriptor.port, 10) || DEFAULT_DB_PORTS[engine] || 3306,
        database: descriptor.database || '',
        username: descriptor.user || '',
      };

  // Absent password key = "ask when connecting". Only write one when there is
  // actually a password to keep, or every connection would silently become a
  // remembered-empty-password one.
  //
  // `forgetPassword` has to be an explicit instruction rather than something
  // read off `remember`. A partial descriptor merges against the stored item,
  // so merely leaving the password out PRESERVES it — which meant unticking
  // "remember the password" wrote a descriptor with remember:false and changed
  // nothing at all, leaving the secret the user asked to drop sitting in the
  // vault and still auto-connecting.
  // `keepPassword` is the matching explicit instruction for "the box was left
  // blank, leave the stored one alone". It says so rather than staying silent
  // for the same reason: an omission is not evidence of intent, and the guest
  // that sends it cannot know whether this save will merge.
  // The password itself is decided in Rust (card 6.4); here only WHICH way.
  // Order matters and is the old one: forget, then keep, then set; with no
  // instruction an existing item keeps what it has (the merge used to).
  let passwordAction = 'keep';
  let password = null;
  if (!isFile && descriptor.forgetPassword) passwordAction = 'forget';
  else if (!isFile && descriptor.keepPassword) passwordAction = 'keep';
  else if (!isFile && descriptor.remember && descriptor.password != null) {
    passwordAction = 'set';
    password = descriptor.password;
  }
  delete data.password;
  // The mode descriptorFromVaultItem showed the DB Manager is the STORED mode
  // with the "Require SSL" floor applied. If the form hands that same value
  // back, the user did not change it: keep what is stored (or nothing), so the
  // floor is never pinned into the item. Without this, a stored `prefer` came
  // back as `require` (or an unset one as the form's default) and unticking
  // "Require SSL" in the vault afterwards changed nothing.
  const unchangedMode =
    !isFile &&
    stored != null &&
    typeof descriptor.sslMode === 'string' &&
    descriptor.sslMode === sslModeWithVaultFloor(base.sslMode, base.ssl);
  if (unchangedMode) {
    if (base.sslMode) data.sslMode = base.sslMode;
    else delete data.sslMode;
  }
  for (const key of ['sslMode', 'sslRootCert', 'sslClientCert', 'sslClientKey']) {
    if (key === 'sslMode' && unchangedMode) continue;
    if (descriptor[key]) data[key] = descriptor[key];
    // Present-but-empty means the user cleared the box — but ONLY on a full
    // descriptor. A partial one never carried TLS at all (the list index has
    // none), so treating its blanks as "cleared" deleted stored certificate
    // paths and silently downgraded verify_full to prefer on any ordinary
    // edit. The edit form is hydrated first precisely so this can be trusted.
    else if (!descriptor.partial && Object.prototype.hasOwnProperty.call(descriptor, key)) {
      delete data[key];
    }
  }

  const name =
    (descriptor.name || '').trim() ||
    (isFile
      // Both separators: sqlite paths on Windows arrive with backslashes, and
      // splitting on '/' alone would name the item after the whole path.
      ? String(descriptor.path || 'SQLite').split(/[\\/]/).pop()
      : `${descriptor.user || 'db'}@${descriptor.host || 'localhost'}`);

  const saved = await invoke('vault_db_connection_save', {
    item: {
      // Absent id creates; the vault mints its own, which is why the caller
      // must use what comes back rather than the id it sent.
      id: descriptor.origin === 'vault' ? descriptor.id : undefined,
      name,
      data,
    },
    passwordAction,
    password,
  });
  return saved?.id || saved;
}

export async function deleteDbConnectionFromVault(itemId) {
  await invoke('vault_delete_item', { id: itemId });
}
